import type { ResolvedFigure } from '../model.js';

import { compileComparison } from './comparison.js';
import { comparisonFixture } from './comparison.fixture.js';
import { compileHierarchy } from './hierarchy.js';
import { hierarchyFixture } from './hierarchy.fixture.js';
import { compileLabelledAnatomy } from './labelled-anatomy.js';
import { labelledAnatomyFixture } from './labelled-anatomy.fixture.js';
import { compileLifecycle } from './lifecycle.js';
import { lifecycleFixture } from './lifecycle.fixture.js';
import { compileMeasurementDimension } from './measurement-dimension.js';
import { measurementDimensionFixture } from './measurement-dimension.fixture.js';
import { compileProportionStrip } from './proportion-strip.js';
import { proportionStripFixture } from './proportion-strip.fixture.js';
import { compileRangeNumberLine } from './range-number-line.js';
import { rangeNumberLineFixture } from './range-number-line.fixture.js';
import { compileTimeline } from './timeline.js';
import { timelineFixture } from './timeline.fixture.js';
import { compileTransform } from './transform.js';
import { transformFixture } from './transform.fixture.js';

export interface PrimitiveGalleryEntry {
  /** Gallery label, marked as a primitive rather than a lesson family. */
  label: string;
  /** The question only this kind of relation can answer. */
  question: string;
  /** The subject the fixture is drawn from, to show the layer is not Go-specific. */
  subject: string;
  spec: unknown;
  figure: ResolvedFigure;
}

/**
 * The nine subject-agnostic primitives, each compiled from a fixture drawn from
 * a different field. They are listed beside the lesson families for comparison
 * and are deliberately not members of the FigureSpec union: this layer is still
 * a spike, and nothing here can break a shipping family.
 */
export function compilePrimitiveGallery(width = 560): PrimitiveGalleryEntry[] {
  const entries: PrimitiveGalleryEntry[] = [];
  const add = <Spec>(label: string, question: string, subject: string, spec: Spec, compile: (value: Spec) => ResolvedFigure): void => {
    entries.push({ label, question, subject, spec, figure: compile(spec) });
  };

  add('Hierarchy (primitive)', 'What does each step down exclude?', 'Biological classification', hierarchyFixture(width), compileHierarchy);
  add('Comparison (primitive)', 'Which criterion actually decides?', 'Conductor materials', comparisonFixture(width), compileComparison);
  add('Transform (primitive)', 'What survives the change unchanged?', 'Photosynthesis', transformFixture(width), compileTransform);
  add('Lifecycle (primitive)', 'Where does the cycle close?', 'The water cycle', lifecycleFixture(width), compileLifecycle);
  add('Labelled anatomy (primitive)', 'Which part sits where, and how thick is it?', 'Earth interior', labelledAnatomyFixture(width), compileLabelledAnatomy);
  add('Proportion strip (primitive)', 'How small is the smallest part, really?', 'Dry air', proportionStripFixture(width), compileProportionStrip);
  add('Range / number line (primitive)', 'What does one step along the scale cost?', 'The pH scale', rangeNumberLineFixture(width), compileRangeNumberLine);
  add('Measurement (primitive)', 'Do the number and the drawn length agree?', 'ISO 216 paper', measurementDimensionFixture(width), compileMeasurementDimension);
  add('Timeline (primitive)', 'How big are the gaps between the moments?', 'Apollo 11', timelineFixture(width), compileTimeline);

  return entries;
}
